import { Controller } from "@hotwired/stimulus"

/**
 * CFA Retry Controller
 *
 * Handles retrying failed Cash Flow Analysis with proper button state management.
 *
 * @example
 * <button data-controller="cfa-retry"
 *         data-action="click->cfa-retry#retry"
 *         data-cfa-retry-application-id-value="123"
 *         data-cfa-retry-retry-url-value="/application/123/cfa/retry">
 *   Retry Analysis
 * </button>
 */
export default class extends Controller {
  static values = {
    applicationId: String,
    retryUrl: String
  }

  async retry(event) {
    event.preventDefault()

    const button = event.currentTarget

    if (!this.hasRetryUrlValue) {
      console.error("[CFA Retry] No retry URL configured")
      return
    }

    console.log("[CFA Retry] Retrying analysis for application:", this.applicationIdValue)

    // Disable button and show loading state
    button.disabled = true
    button.classList.add("opacity-50", "cursor-not-allowed")

    const originalText = button.innerHTML
    button.innerHTML = `
      <svg class="animate-spin h-4 w-4 mr-2 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      Retrying...
    `

    try {
      const response = await fetch(this.retryUrlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        credentials: "same-origin"
      })

      if (response.ok) {
        const data = await response.json()
        console.log("[CFA Retry] Retry queued successfully")

        // Show success message
        this.showMessage("Analysis queued for retry. Please wait...", "success")

        // Reload page after brief delay to show processing state
        setTimeout(() => {
          window.location.reload()
        }, 1500)
      } else {
        const data = await response.json()
        console.error("[CFA Retry] Retry failed:", data.error)

        // Show error message
        this.showMessage(data.error || "Failed to retry analysis. Please try again.", "error")

        // Re-enable button
        button.disabled = false
        button.classList.remove("opacity-50", "cursor-not-allowed")
        button.innerHTML = originalText
      }
    } catch (error) {
      console.error("[CFA Retry] Error:", error)

      // Show error message
      this.showMessage("Network error. Please check your connection and try again.", "error")

      // Re-enable button
      button.disabled = false
      button.classList.remove("opacity-50", "cursor-not-allowed")
      button.innerHTML = originalText
    }
  }

  showMessage(message, type = "info") {
    // Create a temporary flash message
    const flashContainer = document.getElementById("flash-messages") || document.body

    const bgColor = type === "error" ? "bg-red-50 border-red-200 text-red-800" : "bg-green-50 border-green-200 text-green-800"

    const alert = document.createElement("div")
    alert.className = `${bgColor} border px-4 py-3 rounded relative mb-4 animate-fade-in`
    alert.setAttribute("role", "alert")
    alert.innerHTML = `
      <span class="block sm:inline">${message}</span>
    `

    flashContainer.appendChild(alert)

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      alert.classList.add("animate-fade-out")
      setTimeout(() => alert.remove(), 300)
    }, 5000)
  }
}
